import { useCallback, useRef, useState } from "react";
import { useHotkeys } from "react-hotkeys-hook";
import type { AmpLibraryFileFragment } from "src/graphql";
import { isInOverlay } from "src/pages/projects/board-shortcuts";
import { toggleSelection } from "src/pages/projects/task-view";

export type FileSelection = ReturnType<typeof useFileSelection>;

/** Marks an open picker or confirmation for the selection; it owns Escape while it is mounted, wherever focus is. */
export const SELECTION_OVERLAY = "data-selection-overlay";

// Chakra's initial focus runs before a lazy, portaled body mounts, so focus would stay on the menu button that opened it, which takes Escape and clears the checked files.
// Focus goes to the first text box when there is one, else the popover itself: never a folder row, which a stray Enter would pick.
export function focusPopoverBody(body: HTMLElement | null) {
  const popover = body?.closest<HTMLElement>('[role="dialog"]');
  // A frame on, after a closing menu has handed focus back to its button. Chakra may have focused the popover itself, which still needs the move.
  if (body && popover)
    requestAnimationFrame(() => {
      if (!body.contains(document.activeElement))
        (body.querySelector<HTMLElement>("input") ?? popover).focus();
    });
}

/** The files checked for a bulk action. A shift-click checks a range in the order shown, and Escape clears them. */
export function useFileSelection(
  listed: AmpLibraryFileFragment[],
  /** False while a new filter's first page loads or after a fetch fails, when the list is empty only for now. */
  isCurrent: boolean,
) {
  const [ids, setIds] = useState<ReadonlySet<string>>(() => new Set());
  // The last list that loaded stands in until the next one does, so the checked files and the bar stay put.
  const lastListed = useRef(listed);
  if (isCurrent) lastListed.current = listed;
  const files = lastListed.current;
  const selected = files.filter((file) => ids.has(file.id));
  // Only files still listed: one a filter hides or a move takes out of the folder drops out, so it cannot come back checked.
  if (isCurrent && selected.length !== ids.size)
    setIds(new Set(selected.map((file) => file.id)));
  // The last box clicked, where a shift-click range starts.
  const anchorId = useRef<string | null>(null);
  const toggle = (id: string, isRange: boolean) => {
    const from = anchorId.current;
    anchorId.current = id;
    setIds(
      toggleSelection({
        selected: ids,
        orderedIds: files.map((file) => file.id),
        taskId: id,
        anchorId: from,
        isRange,
      }),
    );
  };
  const set = (next: string[]) => setIds(new Set(next));
  // Stable, so the chat's selection chip can hold it without re-registering every render.
  const clear = useCallback(() => setIds(new Set()), []);
  useHotkeys("escape", clear, {
    enabled: selected.length > 0,
    // Focus stays on the box just checked, so Escape works from a checkbox, though not from the search.
    enableOnFormTags: ["input"],
    // A dialog or menu closes on its own Escape and leaves the selection alone.
    ignoreEventWhen: (event) =>
      event.defaultPrevented ||
      isInOverlay(event) ||
      document.querySelector(`[${SELECTION_OVERLAY}]`) !== null ||
      (event.target instanceof HTMLInputElement &&
        event.target.type !== "checkbox"),
  });
  return {
    selected,
    isSelected: (id: string) => ids.has(id),
    toggle,
    set,
    isAllSelected: files.length > 0 && selected.length === files.length,
    selectAll: () => set(files.map((file) => file.id)),
    clear,
    deselect: (removed: string[]) =>
      setIds(new Set([...ids].filter((id) => !removed.includes(id)))),
  };
}
